import { Backdrop, CircularProgress, CssBaseline, ThemeProvider } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { useGetFeatureFlagsQuery } from './api/features';
import { RootModal } from './app/components/root-modal';
import { loadConfig } from './app/reducers';
import { clearAuthorization, loadLocalStorageSession } from './features/account/reducer/session';
import { setExpiredAuthCallback } from './general/utils/auth-token';
import { getRouteDefinitions } from './routes';
import { configureAxios } from './services/authorization.service';
import { RootState } from './store';
import { EmbeddedThemeValue } from './theme/types';
import { buildTheme, resolveTheme } from './theme/utils';
import { getPortalType } from './utils';

export const router = createBrowserRouter(getRouteDefinitions(getPortalType()));

const selectAuthorized = (state: RootState) => state.session.authorized;

const App: FC = () => {
  const authorized = useSelector(selectAuthorized);
  const { data: featureFlags, refetch: refetchFlags } = useGetFeatureFlagsQuery();
  const [initializing, setInitializing] = useState(true);
  const dispatch = useDispatch();
  const [themeMode, setThemeMode] = useState<EmbeddedThemeValue>({ theme: 'coast' });

  useEffect(() => {
    configureAxios(() => dispatch(clearAuthorization()));
    dispatch(loadConfig());
    dispatch(loadLocalStorageSession());
    setExpiredAuthCallback(() => dispatch(clearAuthorization()));
  }, [dispatch]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setThemeMode(resolveTheme(params.get('theme')));
  }, []);

  useEffect(() => {
    void refetchFlags(); // refetch when authorization changes
  }, [authorized, refetchFlags]);

  useEffect(() => {
    setInitializing(!featureFlags);
  }, [featureFlags]);

  const selectedTheme = useMemo(() => buildTheme(themeMode), [themeMode]);

  return (
    <ThemeProvider theme={selectedTheme}>
      <CssBaseline />
      {initializing ? (
        <Backdrop open>
          <CircularProgress color="inherit" data-testid="Loading indicator" />
        </Backdrop>
      ) : (
        <RouterProvider
          router={router}
          fallbackElement={
            <Backdrop open>
              <CircularProgress color="inherit" />
            </Backdrop>
          }
        />
      )}
      <RootModal router={router} />
    </ThemeProvider>
  );
};

export default App;
